import { AnimatePresence, motion } from "motion/react"
import { HugeiconsIcon, Icons } from "@/components/icons"

import { NotificationListPanel } from "./NotificationListPanel"
import { Separator } from "@/components/ui/separator"
import { UserDisplayOrConnectButton } from "@/components/layout/UserDisplayOrConnectButton"
import { WordRotate } from "@/components/ui/word-rotate"
import { cn } from "@/lib/utils"
import { useMemo } from "react"
import { useNotificationContext } from "@/contexts/notification-context"

const pillClass =
  "border-border bg-card flex items-center gap-3 rounded-2xl border px-4 py-3 shadow-1-light"

const innerPillClass =
  "bg-secondary-background flex items-center gap-2 rounded-xl px-[10px] py-2 h-10 hover:bg-primary-25 text-primary-text"

const transition = {
  duration: 0.45,
  ease: [0.32, 0.72, 0, 1],
} as const

export interface HeaderNotificationsProps {
  /** Optional warning/alert text (e.g. from API). Shown when there are no unread in-app notifications. */
  warningMessage?: string | null
}

/**
 * First pill: alert area (rotating unread titles or warningMessage) + notification list + user.
 */
export function HeaderNotifications({
  warningMessage,
}: HeaderNotificationsProps) {
  const { notifications, unreadCount } = useNotificationContext()

  const unreadTitles = useMemo(
    () => notifications.filter((n) => !n.read).map((n) => n.title),
    [notifications]
  )
  const hasUnread = unreadCount > 0
  const showPill = hasUnread || !!warningMessage

  return (
    <motion.div
      layout
      transition={{ layout: transition }}
      className={cn(pillClass, "gap-2")}
    >
      <AnimatePresence initial={false} mode="wait">
        {showPill && (
          <motion.div
            layout
            initial={{
              opacity: 0,
              width: 0,
              minWidth: 0,
              clipPath: "inset(0 100% 0 0)",
            }}
            animate={{
              opacity: 1,
              width: "auto",
              minWidth: "min-content",
              clipPath: "inset(0 0 0 0)",
            }}
            exit={{
              opacity: 0,
              width: 0,
              minWidth: 0,
            }}
            transition={transition}
            className={cn(
              innerPillClass,
              "bg-warning-5 hover:bg-warning-5 shrink-0 overflow-hidden"
            )}
            title={hasUnread ? unreadTitles[0] : (warningMessage ?? undefined)}
          >
            <HugeiconsIcon
              icon={Icons.Alert01}
              size={20}
              className="text-warning shrink-0"
            />
            {hasUnread ? (
              <motion.div
                layout
                transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                className="text-primary-text app-text-body-small max-w-[180px] min-w-0 truncate sm:max-w-[240px] lg:max-w-[280px]"
              >
                <WordRotate
                  words={unreadTitles.length > 0 ? unreadTitles : [""]}
                  duration={3000}
                  className="text-inherit"
                />
              </motion.div>
            ) : (
              <span className="text-primary-text app-text-body-small max-w-[180px] truncate sm:max-w-[240px] lg:max-w-[280px]">
                {warningMessage}
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <NotificationListPanel
        triggerClassName={cn(innerPillClass, showPill && "relative")}
      />
      <Separator orientation="vertical" className="h-10! w-px" />
      <UserDisplayOrConnectButton />
    </motion.div>
  )
}
